'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { BlogPost } from '@/app/types/blog'

interface UseBlogPostsQueryProps {
	language: string
	categorySlug?: string
	initialPosts: BlogPost[]
	initialTotal: number
	limit?: number
}

import { BLOG_POSTS_PER_PAGE } from '@/app/constants'

interface BlogPostsPage {
	posts: BlogPost[]
	offset: number
	hasMore: boolean
}

// Production: 9 posts per page
const POSTS_PER_PAGE = BLOG_POSTS_PER_PAGE

export const useBlogPostsQuery = ({
	language,
	categorySlug,
	initialPosts,
	initialTotal,
	limit = POSTS_PER_PAGE,
}: UseBlogPostsQueryProps) => {
	const queryKey = categorySlug
		? ['blog-posts', language, categorySlug]
		: ['blog-posts', language]

	return useInfiniteQuery<BlogPostsPage, Error>({
		queryKey,
		queryFn: async ({ pageParam = limit }) => {
			const offset = pageParam as number
			const url = categorySlug
				? `/api/blog/posts?language=${language}&categorySlug=${categorySlug}&offset=${offset}&limit=${limit}`
				: `/api/blog/posts?language=${language}&offset=${offset}&limit=${limit}`

			const response = await fetch(url)
			if (!response.ok) {
				throw new Error('Failed to fetch blog posts')
			}

			const data = await response.json()
			return {
				posts: data.posts,
				offset,
				hasMore: data.hasMore,
			}
		},
		getNextPageParam: (lastPage, allPages) => {
			if (!lastPage.hasMore) {
				return undefined
			}
			// Calculate next offset: sum of all posts loaded so far
			const totalLoaded = allPages.reduce(
				(sum, page) => sum + page.posts.length,
				0
			)
			return totalLoaded
		},
		initialPageParam: limit,
		initialData: {
			pages: [
				{
					posts: initialPosts,
					offset: 0,
					hasMore: initialTotal > initialPosts.length,
				},
			],
			pageParams: [limit],
		},
		staleTime: 1000 * 60 * 5, // 5 minutes
		refetchOnWindowFocus: false,
	})
}
